<template>
  <nav
    ref="itemNavigationList"
    class="ah-items-navigation"
    role="navigation"
    :aria-label="navAriaLabel"
    @keydown.esc="closeMegaMenu"
  >
    <AhList
      ref="ahlistnav"
      :column="false"
      marge="large"
    >
      <AhListItem
        v-for="item in mainItems"
        :key="item.id"
      >
        <AhMenuItem
          :key="item.id"
          :tracking="item.tracking"
          :target="item.url && item.target"
          :label="item.text"
          :aria-controls="item.url ? undefined : item.id"
          :url="item.url || null"
          :active="item.id === activeItem"
          :aria-expanded="!item.url ? String(item.id === activeItem) : null"
          variation="menu-item"
          @clicked="clickHandler(item)"
        />
        <Transition
          :name="isMobile ? 'ah-fade' : ''"
        >
          <AhMegaMenu
            v-show="(!item.url && activeItem === item.id) || fromPre"
            :id="item.id"
            :ref="'ahmegamenu-'+item.id"
            :aria-label="item.text"
            :class="{ 'ah-mega-menu--open': item.id === activeItem }"
            :visible="activeItem === item.id || fromPre"
            :list-items="item.panels"
            :list-push-items="item.pushs"
            :title="item.text"
            :tracking="item.tracking"
            @click:ahbar:back="closeMegaMenu"
            @megaMenu:itemClicked="megaMenuItemClicked"
            @pushClicked="megaMenuPushClicked"
            @keydown.tab.native="tabKeyHandler"
          />
        </Transition>
      </AhListItem>
      <AhListItem
        v-show="!isMobile && moreItems.length > 0"
      >
        <AhMoreNav
          :list-items="moreItems"
          @tabs-nav-item-click="TabsMegaMeunclickHandler"
          @open-more-nav="toggleTabMore"
        />
      </AhListItem>
    </AhList>
  </nav>
</template>

<script>
  import AhList from '../AhList/AhList.vue';
  import AhListItem from '../AhList/_internal/AhListItem.vue';
  import AhMenuItem from '../../molecules/AhMenuItem/AhMenuItem.vue';
  import AhMegaMenu from '../AhMegaMenu/AhMegaMenu.vue';
  import AhMoreNav from '../AhMoreNav/AhMoreNav.vue';
  import { NAV_ARIA_LABELS } from '../../../constants/wording.js';

  export default {
    name: 'AhItemsNavigation',
    components: {
      AhMoreNav,
      AhMegaMenu,
      AhList,
      AhListItem,
      AhMenuItem
    },
    inject: {
      fromPre: {
        from: 'prerendering',
        default: false
      },
      lang: {
        default: () => function() { return 'en'; },
      }
    },
    props: {
      burgermenuState : {
        type: Boolean,
        default : false
      },
      mainItems: {
        type: Array,
        required: true,
      },
      moreItems: {
        type: Array,
        default: () => [],
      },
      isMobile: {
        type: Boolean,
        default : false
      },
      ariaLabel: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        activeItem: null,
        position: '',
      };
    },
    computed: {
      openItem () {
        return this.mainItems.find(l => l.id === this.activeItem);
      },
      focusableElements () {
        if ((this.openItem && !this.openItem.url && this.openItem?.panels?.length) || this.openItem?.pushs?.length) {
          const focusables = this.getItemElements(this.openItem)
            .map(it => it.id)
            .join(', #');
          const elements = document.querySelectorAll(`#${focusables}`);
          return [...elements].filter(el => el.offsetParent !== null);
        }
        return [];
      },
      navAriaLabel () {
        return this.ariaLabel || NAV_ARIA_LABELS[this.lang];
      }
    },
    mounted() {
      document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
      handleClickOutside(event) {
        if (!this.$refs?.ahlistnav?.$el?.contains(event.target) && this.activeItem !== null) {
          this.closeEmitter();
        }
      },
      toggleTabMore(data) {
        this.position = this.mainItems.length + 1;
        this.$emit('nav:panel:clicked', {...data, more:true, position: this.position || ''});
        this.activeItem = null;
      },
      TabsMegaMeunclickHandler (data) {
        this.$emit('menu:panel:clicked', {...data, position: this.position});
      },
      async clickHandler (item) {
        const { id, text, tracking } = item;
        this.position = this.mainItems.findIndex(itemm => itemm.id === id) + 1;
        this.$emit('nav:panel:clicked', {
          id,
          text,
          tracking,
          position: this.position || '',
          opened: this.activeItem !== item.id,
        });
        if (item.url) return;
        
        if (this.activeItem === item.id) {
          this.closeEmitter();
        } else {
          this.activeItem = item.id;
        }
        if (!this.activeItem) return;
        await this.$nextTick();
        this.focusableElements.length && this.focusableElements[0].focus();
      },
      async tabKeyHandler (e) {
        if (e.key && e.key === 'Tab') {
          if (!this.openItem) return;
          if (!this.focusableElements.length) return;
          if (document.activeElement === this.focusableElements.at(-1) && !e.shiftKey) {
            this.closeEmitter();
          }
          if (e.shiftKey && document.activeElement) {
            if (
              (document.activeElement.id === 'ah_bar_lang') ||
              (window.innerWidth > 767 && this.getItemElements(this.openItem)[0].id === document.activeElement.id)
            ) {
              this.closeEmitter();
            }
          }
        }
      },
      getItemElements (el) {
        let elements = [];
        if (el.panels && el.panels.length) elements = [...el.panels.flat()];
        if (el.pushs && el.pushs.length) elements = [...elements, ...el.pushs];
        return elements;
      },
      clickOutsideHandler() {
        if (this.isMobile) return;
        this.activeItem = null;
      },
      closeMegaMenu (e) {
        if (!this.activeItem) return;
        this.$nextTick(() =>{
          const elementToReceiveFocus = document.querySelector(`button[aria-controls="${this.activeItem}"]`);
          if (elementToReceiveFocus) {
            elementToReceiveFocus.focus();
          }
          this.closeEmitter();
        });
        if(e) {
          e.preventDefault();
        }
      },
      megaMenuItemClicked (data) {
        this.$emit('menu:panel:clicked', { ...data, position: this.position });
      },
      megaMenuPushClicked (data) {
        this.$emit('menu:panel:clicked', { ...data, position: this.position });
      },
      closeEmitter () {
        this.$emit('nav:panel:closed', {
          id: this.openItem?.id,
          tracking: this.openItem?.tracking,
          text: this.openItem?.text,
          opened: false
        });
        this.activeItem = null;
      }
    },
  };
</script>
